import type { GlucoseActivity } from "@tandemdiabetes/sugarmate-api-react-client"
import {
  Slider,
  SliderInput,
  SliderTracks,
  SliderValue,
  SliderValueInput,
  SliderValueLabel,
  SliderValueLabelContainer,
} from "@tandemdiabetes/sugarmate-react-components"
import { useCallback } from "react"

import {
  useSliderInputProps,
  useReadableGlucoseUnit,
  useGetGlucoseValue,
  useSetGlucoseValue,
  useGlucoseInputProps,
} from "src/hooks"
import styles from "src/styles/entryModal.module.scss"

const inputProps = {
  min: 20,
  max: 500,
}

interface Props {
  activity?: Partial<GlucoseActivity>;
  updateActivity: (a: Partial<GlucoseActivity>) => void;
  readOnly?: boolean;
}

const id = "glucose-range-input"
const glucoseRangeDetailId = "number-range-input"

export function GlucoseRangeInput(props: Props) {
  const {
    activity,
    updateActivity,
    readOnly = false,
  } = props

  const glucoseUnit = useReadableGlucoseUnit()
  const getGlucoseValue = useGetGlucoseValue()
  const setGlucoseValue = useSetGlucoseValue()
  const sharedProps = useGlucoseInputProps(inputProps)

  const onChange = useCallback(
    (newValue: number | null) => {
      updateActivity({
        mg_dl: typeof newValue === "number"
          ? setGlucoseValue(newValue)
          : undefined,
      })
    },
    [
      updateActivity,
      setGlucoseValue,
    ],
  )

  const value = activity?.mg_dl !== undefined
    ? getGlucoseValue(activity.mg_dl)
    : null

  const {
    inputValue,
    sliderValue,
    onNumberChange,
    onSliderChange,
    onNumberBlur,
    onNumberKeyDown,
  } = useSliderInputProps({
    min: sharedProps.min,
    max: sharedProps.max,
    inputStep: sharedProps.step,
    sliderStep: sharedProps.step,
    value,
    onChange,
    allowEmptyValue: true,
    emptyValue: null,
  })

  return (
    <div className={styles.row}>
      <SliderValue sizePreset="large">
        <SliderValueInput
          {...sharedProps}
          value={inputValue}
          placeholder="---"
          id={glucoseRangeDetailId}
          onChange={onNumberChange}
          onBlur={onNumberBlur}
          onKeyDown={onNumberKeyDown}
          readOnly={readOnly}
          required
        />
        <SliderValueLabelContainer>
          <SliderValueLabel
            htmlFor={id}
            id="glucose-range-input-label"
          >
            {glucoseUnit}
          </SliderValueLabel>
        </SliderValueLabelContainer>
      </SliderValue>
      {!readOnly && (
        <Slider className={styles.rangeInputSlider}>
          <SliderInput
            {...sharedProps}
            id={id}
            value={sliderValue ?? sharedProps.min}
            onChange={onSliderChange}
          />
          <SliderTracks
            min={sharedProps.min}
            max={sharedProps.max}
          />
        </Slider>
      )}
    </div>
  )
}
